import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { Confirm } from '../components/Confirm';
import { ConexionMercadoLibre } from '../components/ConexionMercadoLibre';
import { TraerPlanillaCodigos } from '../components/TraerPlanillaCodigos';
import { ImportacionesYMantenimientoBase } from '../components/ImportacionesYMantenimientoBase';
import { SkuEnMercadoLibre } from './SkuEnMercadoLibre';
import { MENSAJE_ENCENDER_DEPOSITO_FULL, SWITCH_DEPOSITO_FULL } from './VistaPreviaSyncSku';

const URL_BASE = '/api/v3/ml/sync-sku';
const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('es-AR'));

/** Última corrida automática de «Sincronizar solos los combos listos» (backend: CorridaCombosAutomaticos). */
interface CorridaCombos {
  en: string; listos: number; encendidos: number; omitidos: number; errores: number; pendientes: number;
  /** Detalle técnico (sólo en el title). */
  motivos: string[];
  saltados?: Array<{ sku: string; codigo: CodigoComboSaltado }>;
  error: string | null;
}
type CodigoComboSaltado = 'no_listo' | 'composicion_cambio' | 'error';
/** GET config de la sincronización por SKU. */
interface ConfigSync { escrituraDepositoFull: boolean; combosListosAutomatico?: boolean; ultimaCorridaCombos?: CorridaCombos | null }
/** Un combo listo de GET combos-sin-sincronizar (lo que muestra la confirmación de encender el switch). */
interface ComboListo { sku: string; mayorCambio: { mlaId: string; stockMl: number | null; sePublica: number | null } | null }

export const SWITCH_COMBOS_LISTOS = 'Sincronizar solos los combos listos';
const AYUDA_COMBOS_LISTOS = 'Cuando todos los componentes de un combo ya sincronizan y están contados, el combo empieza a sincronizar solo. '
  + 'Puede cambiar de golpe el stock que muestra Mercado Libre en esas publicaciones.';
/** Combos por corrida automática (MAX_COMBOS_LOTE en el backend). */
const COMBOS_POR_CORRIDA = 100;

/** Confirmación de encender el switch: la explicación, cuántos combos están listos hoy y los 3 cambios más grandes en ML. */
function mensajeEncenderCombos(listos: ComboListo[] | null): string {
  if (listos === null) return AYUDA_COMBOS_LISTOS;
  if (listos.length === 0) {
    return `${AYUDA_COMBOS_LISTOS}\n\nHoy no hay combos listos: van a empezar solos a medida que sus componentes sincronicen y se cuenten.`;
  }
  const cambios = listos
    .flatMap((l) => {
      const c = l.mayorCambio;
      return c && c.stockMl !== null && c.sePublica !== null && c.stockMl !== c.sePublica ? [{ sku: l.sku, ...c, salto: Math.abs(c.sePublica - c.stockMl) }] : [];
    })
    .sort((a, b) => b.salto - a.salto)
    .slice(0, 3)
    .map((c) => `• ${c.sku} (${c.mlaId}): de ${n(c.stockMl)} a ${n(c.sePublica)}`);
  const cuantos = listos.length === 1 ? 'Hoy hay 1 combo listo: empieza' : `Hoy hay ${n(listos.length)} combos listos: empiezan`;
  const tanda = listos.length > COMBOS_POR_CORRIDA ? ` (de a ${COMBOS_POR_CORRIDA} cada 15 minutos)` : '';
  return `${AYUDA_COMBOS_LISTOS}\n\n${cuantos} a sincronizar en los próximos 15 minutos${tanda}.`
    + (cambios.length > 0 ? `\nLos cambios más grandes en Mercado Libre (lo que muestra hoy → lo que va a mostrar):\n${cambios.join('\n')}` : '');
}

/** "hoy 14:05" o "31/10 14:05". */
function diaYHora(iso: string): string {
  const d = new Date(iso);
  const hora = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return d.toDateString() === new Date().toDateString() ? `hoy ${hora}` : `${d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })} ${hora}`;
}

/** Por qué no empezaron (singular, plural), por código de la corrida (backend: CodigoComboSaltado). */
const PORQUE_SALTADO: Record<CodigoComboSaltado, [string, string]> = {
  no_listo: ['porque le falta sincronizar o contar un componente', 'porque les falta sincronizar o contar un componente'],
  composicion_cambio: ['porque le cambió la composición', 'porque les cambió la composición'],
  error: ['por un error', 'por errores'],
};
/** SKU nombrados por motivo; el resto, "y N más". */
const SKUS_POR_MOTIVO = 5;

/** "Última corrida (hoy 14:05): empezaron a sincronizar 5 combos. 2 no se pudieron porque … Quedan 30 para las próximas corridas." */
function resumenCorridaCombos(c: CorridaCombos): string {
  const cuando = diaYHora(c.en);
  if (c.error) return `La última corrida (${cuando}) no pudo terminar (se reintenta sola en 15 minutos).`;
  const saltados = c.saltados ?? [];
  const inicio = c.encendidos > 0
    ? (c.encendidos === 1 ? 'empezó a sincronizar 1 combo' : `empezaron a sincronizar ${n(c.encendidos)} combos`)
    : saltados.length > 0 ? 'no empezó ningún combo' : 'no había combos listos';
  const grupos = (Object.keys(PORQUE_SALTADO) as CodigoComboSaltado[]).flatMap((codigo) => {
    const skus = saltados.filter((x) => x.codigo === codigo).map((x) => x.sku);
    if (skus.length === 0) return [];
    const resto = skus.length - SKUS_POR_MOTIVO;
    const cuales = skus.slice(0, SKUS_POR_MOTIVO).join(', ') + (resto > 0 ? ` y ${n(resto)} más` : '');
    const [uno, varios] = PORQUE_SALTADO[codigo];
    return [`${skus.length === 1 ? `1 no se pudo ${uno}` : `${n(skus.length)} no se pudieron ${varios}`}: ${cuales}.`];
  });
  const quedan = c.pendientes === 0 ? [] : [c.pendientes === 1 ? 'Queda 1 para la próxima corrida.' : `Quedan ${n(c.pendientes)} para las próximas corridas.`];
  return [`Última corrida (${cuando}): ${inicio}.`, ...grupos, ...quedan].join(' ');
}

/** La última corrida de «Sincronizar solos los combos listos»: el resumen y el detalle técnico en el title. */
function UltimaCorridaCombos({ c }: { c: CorridaCombos }) {
  const detalle = c.error ?? (c.motivos.length > 0 ? c.motivos.join('\n') : undefined);
  return <p className="text-xs text-slate-500" title={detalle}>{resumenCorridaCombos(c)}</p>;
}

/** Configuración → Mercado Libre y catálogo. Mercado Libre vuelve acá (?ml=conectado|error) después de autorizar. */
export function ConfiguracionMercadoLibrePage() {
  const { user, can } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const resultado = searchParams.get('ml');
  const vuelveDeConectar = resultado === 'conectado' || resultado === 'error';

  const esAdmin = user?.role === 'admin';
  const [depositoFull, setDepositoFull] = useState<boolean | null>(null);
  const [combosListos, setCombosListos] = useState<boolean | null>(null);
  const [corridaCombos, setCorridaCombos] = useState<CorridaCombos | null>(null);
  /** El mensaje de la confirmación de «Sincronizar solos los combos listos» (con los combos listos de hoy). */
  const [mensajeCombos, setMensajeCombos] = useState('');
  const [confirmarSwitch, setConfirmarSwitch] = useState<'deposito' | 'combos' | null>(null);

  useEffect(() => {
    api.get<ConfigSync>(`${URL_BASE}/config`).then((c) => {
      setDepositoFull(c?.escrituraDepositoFull === true);
      setCombosListos(c?.combosListosAutomatico === true);
      setCorridaCombos(c?.ultimaCorridaCombos ?? null);
    }).catch(() => {});
  }, []);

  const guardarDepositoFull = async (valor: boolean) => {
    setConfirmarSwitch(null);
    try {
      const c = await api.post<{ escrituraDepositoFull: boolean }>(`${URL_BASE}/deposito-full`, { escrituraDepositoFull: valor });
      setDepositoFull(c?.escrituraDepositoFull === true);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  /** Antes de encender: los combos listos de hoy para la confirmación (si no se pueden leer, sólo la explicación). */
  const pedirEncenderCombos = async () => {
    const listos = await api.get<{ listos: ComboListo[] }>(`${URL_BASE}/combos-sin-sincronizar?porPagina=1`).then((r) => r?.listos ?? null, () => null);
    setMensajeCombos(mensajeEncenderCombos(listos));
    setConfirmarSwitch('combos');
  };
  const guardarCombosListos = async (valor: boolean) => {
    setConfirmarSwitch(null);
    try {
      const c = await api.post<ConfigSync>(`${URL_BASE}/combos-automaticos`, { combosListosAutomatico: valor });
      setCombosListos(c?.combosListosAutomatico === true);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  useEffect(() => {
    if (!vuelveDeConectar) return;
    if (resultado === 'conectado') toast.addToast('Mercado Libre conectado', 'success');
    else toast.addToast('No se pudo conectar Mercado Libre (el enlace puede haber vencido o Mercado Libre rechazó la autorización). Volvé a apretar "Conectar / reconectar Mercado Libre"; si sigue fallando, revisá el log del servidor.', 'error');
    setSearchParams({}, { replace: true });
  }, [vuelveDeConectar]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-800">Mercado Libre y catálogo</h1>
      <section aria-label="Conexión con Mercado Libre">
        <ConexionMercadoLibre esAdmin={esAdmin} />
      </section>
      <section aria-label="Planilla 02_CODIGOS">
        <TraerPlanillaCodigos puedeTraer={can('catalogo', 'eliminar')} />
      </section>
      <section aria-label="Interruptores de Mercado Libre" className="space-y-3">
        <h2 className="text-base font-semibold text-slate-800">Interruptores de Mercado Libre</h2>
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
          <label className="flex items-center gap-2 text-sm text-slate-800">
            <input type="checkbox" checked={depositoFull === true} disabled={!esAdmin || depositoFull === null}
              onChange={(e) => (e.target.checked ? setConfirmarSwitch('deposito') : void guardarDepositoFull(false))} />
            {SWITCH_DEPOSITO_FULL}
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-800">
            <input type="checkbox" checked={combosListos === true} disabled={!esAdmin || combosListos === null}
              onChange={(e) => (e.target.checked ? void pedirEncenderCombos() : void guardarCombosListos(false))} />
            {SWITCH_COMBOS_LISTOS}
          </label>
          <p className="text-xs text-slate-500">{AYUDA_COMBOS_LISTOS}</p>
          {corridaCombos && <UltimaCorridaCombos c={corridaCombos} />}
          {!esAdmin && <p className="text-xs text-slate-500">Sólo un administrador puede cambiarlos.</p>}
        </div>
        <SkuEnMercadoLibre esAdmin={esAdmin} />
      </section>
      <ImportacionesYMantenimientoBase esAdmin={esAdmin} />
      <Confirm
        open={confirmarSwitch === 'deposito'}
        title={SWITCH_DEPOSITO_FULL}
        message={MENSAJE_ENCENDER_DEPOSITO_FULL}
        confirmLabel="Encender"
        onCancel={() => setConfirmarSwitch(null)}
        onConfirm={() => void guardarDepositoFull(true)}
      />
      <Confirm
        open={confirmarSwitch === 'combos'}
        title={SWITCH_COMBOS_LISTOS}
        message={mensajeCombos}
        confirmLabel="Encender"
        onCancel={() => setConfirmarSwitch(null)}
        onConfirm={() => void guardarCombosListos(true)}
      />
    </div>
  );
}
